import React, { useContext, useEffect, useState } from 'react';
import { SmartSendItemsCache } from '~/apps/smartsend/lib/cache/SmartSendItemsCache';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { SmartSendItemsUpdatedSocketAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { HasExternalTrackingNumber } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { P2GFade, P2GLink, P2GLinkGroup, P2GProgress, P2GText } from '@parcel2go/core-ui';
import AlertWithIcon from '../../AlertWithIcon';

interface props {
  hasPaid: boolean;
  items: SmartSendItemApiModel[];
  onAllTrackingNumbersGenerated?: () => void;
  onCustomerWantsToSeeTrackingNumbers?: () => void;
  wrapper: (x: React.ReactNode) => React.ReactElement;
}

const ItemsWhichAreBookedCount = (items: SmartSendItemApiModel[]) => {
  const ids = items.map((x) => x.Id);
  return SmartSendItemsCache.filter((x) => ids.includes(x.Id) && HasExternalTrackingNumber(x)).length;
};

const OrderBookingStatusObserver: React.FC<props> = (props) => {
  const { getBookedStatusByIds } = useSmartSendApi();
  const sctx = useContext(SmartSendContext);
  const [takingTooLong, setTakingTooLong] = useState(false);
  const itemsToConsider = props.items;
  const itemsCount = itemsToConsider.length;
  const itemsBooked = ItemsWhichAreBookedCount(itemsToConsider);
  const hasAllBookingNumbers = itemsBooked === itemsCount;
  const orderIsPaid = props.hasPaid || itemsBooked > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setTakingTooLong(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // If we dont need to wait for tracking numbers, just update the parent saying we're done
  useEffect(() => {
    if (hasAllBookingNumbers && props.onAllTrackingNumbersGenerated) {
      props.onAllTrackingNumbersGenerated();
    }
  }, [hasAllBookingNumbers]);

  // This will run every 7.5 seconds to check if any new tracking numbers have been generated
  // If sockets are enabled, we will get a socket update instead
  useEffect(() => {
    if (itemsToConsider.length == 0 || hasAllBookingNumbers) {
      return;
    }
    const run = async () => {
      const ids = itemsToConsider.filter((x) => !HasExternalTrackingNumber(x)).map((x) => x.Id);
      if (ids.length > 0) {
        const res = await getBookedStatusByIds(ids);
        if (res.success) {
          const newTracking = res.result.Records.filter((x) => x.Booked);
          if (newTracking.length > 0) {
            sctx.dispatch(
              new SmartSendItemsUpdatedSocketAction({
                Items: newTracking.map((r) => {
                  return {
                    Id: r.ItemId,
                    ItemUpdates: { Meta: { ExternalTrackingNumber: { Value: r.TrackingNumber, Type: 'string' } } }
                  };
                })
              })
            );
          }
        }
      }
    };
    const interval = setInterval(run, 7500);
    return () => clearInterval(interval);
  }, [itemsToConsider, hasAllBookingNumbers]);

  if (!orderIsPaid || hasAllBookingNumbers) {
    return null;
  }

  return props.wrapper(
    <P2GFade in>
      <AlertWithIcon
        color={takingTooLong ? 'warning' : 'info'}
        loader={
          <>
            <P2GProgress />
            {takingTooLong && (
              <div>
                <P2GText small>
                  {itemsBooked}/{itemsCount}
                </P2GText>
              </div>
            )}
          </>
        }
      >
        {takingTooLong ? (
          <P2GText variant="body2" component="p">
            It's taking longer than usual getting your labels...
            <P2GLinkGroup
              pt={1}
              links={[
                props.onCustomerWantsToSeeTrackingNumbers && (
                  <P2GLink onClick={props.onCustomerWantsToSeeTrackingNumbers}>Try Labels</P2GLink>
                )
              ]}
            />
          </P2GText>
        ) : (
          <P2GText variant="body2" component="p">
            We're just checking the status of your labels...
          </P2GText>
        )}
      </AlertWithIcon>
    </P2GFade>
  );
};

export default OrderBookingStatusObserver;
